import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import PromissoryNoteService from '../../services/PromissoryNoteService';

export default function OwnPromissoryNoteListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
    });
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth() + 1, 0);
    });
    const [showDialog, setShowDialog] = useState(false);
    const [newNote, setNewNote] = useState({
        amount: 0,
        dueDate: null,
        drawer: 'Firmamız',
        guarantor: '',
        description: '',
        type: 2 // Outbound
    });
    const toast = useRef(null);

    const statusOptions = [
        { label: 'Tüm Durumlar', value: null },
        { label: 'Portföyde', value: 1 },
        { label: 'Verildi', value: 2 },
        { label: 'Tahsil Edildi', value: 3 },
        { label: 'Ödendi', value: 4 },
        { label: 'İade Edildi', value: 5 }
    ];

    const clearFilters = () => {
        const d = new Date();
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        setGlobalFilter('');
        setStatusFilter(null);
        setStartDate(start);
        setEndDate(end);
        fetchNotes(null, start, end);
    };

    const formatDateParam = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
    };

    const fetchNotes = async (status = statusFilter, start = startDate, end = endDate) => {
        try {
            setLoading(true);
            const res = await PromissoryNoteService.getPromissoryNotes(status, 2, formatDateParam(start), formatDateParam(end));
            setNotes(res.data || []);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Senetler yüklenirken hata oluştu' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    const saveNote = async () => {
        if (!newNote.amount || !newNote.dueDate || !newNote.drawer) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen gerekli alanları doldurun' });
            return;
        }

        try {
            const res = await PromissoryNoteService.createPromissoryNote(newNote);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Senet tanımlandı' });
                setShowDialog(false);
                fetchNotes();
                setNewNote({
                    amount: 0,
                    dueDate: null,
                    drawer: 'Firmamız',
                    guarantor: '',
                    description: '',
                    type: 2
                });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Kaydedilirken hata oluştu' });
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statuses = {
            1: { label: 'Portföyde', severity: 'info' },
            2: { label: 'Verildi', severity: 'warning' },
            3: { label: 'Tahsil Edildi', severity: 'success' },
            4: { label: 'Ödendi', severity: 'success' },
            5: { label: 'İade Edildi', severity: 'danger' }
        };
        const status = statuses[rowData.status] || { label: 'Bilinmiyor', severity: 'secondary' };
        return <Tag value={status.label} severity={status.severity} />;
    };

    const filteredNotes = notes.filter((row) => {
        if (statusFilter !== null && Number(row.status) !== Number(statusFilter)) {
            return false;
        }
        if (startDate) {
            const due = new Date(row.dueDate);
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            if (due < start) return false;
        }
        if (endDate) {
            const due = new Date(row.dueDate);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (due > end) return false;
        }
        return true;
    });

    const renderHeader = () => {
        return (
            <div className="flex justify-content-between align-items-center">
                <Button label="Yeni Senet Tanımla" icon="pi pi-plus" className="p-button-sm !rounded-xl !bg-orange-600 border-none" onClick={() => setShowDialog(true)} />
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Borçlu, tedarikçi veya açıklama ara..."
                        className="p-inputtext-sm !py-2.5 !rounded-xl text-xs font-bold w-64"
                    />
                </span>
            </div>
        );
    };

    return (
        <div className="p-4 space-y-4 animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <div className="bg-white shadow-xl border border-slate-200 rounded-3xl overflow-hidden p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="!bg-orange-600 p-4 rounded-xl shadow-lg shadow-orange-600/20">
                            <i className="pi pi-file text-white text-xl"></i>
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-800 dark:text-white m-0">Kendi Senetlerim</h1>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Firmamıza ait tanımlanmış senetler</p>
                        </div>
                    </div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text p-button-secondary rounded-xl hover:bg-slate-100"
                        onClick={fetchNotes}
                        tooltip="Yenile"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-slate-50/80 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 items-end mb-6">
                    <div className="md:col-span-3 flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Senet Durumu</label>
                        <Dropdown
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(e) => setStatusFilter(e.value)}
                            placeholder="Tüm Durumlar"
                            className="w-full rounded-xl border-slate-200 font-bold text-sm h-[42px] flex items-center"
                        />
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Vade Başlangıç</label>
                        <Calendar
                            value={startDate}
                            onChange={(e) => setStartDate(e.value)}
                            showIcon
                            dateFormat="dd.mm.yy"
                            placeholder="Tarih seçin"
                            className="w-full"
                            inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Vade Bitiş</label>
                        <Calendar
                            value={endDate}
                            onChange={(e) => setEndDate(e.value)}
                            showIcon
                            dateFormat="dd.mm.yy"
                            placeholder="Tarih seçin"
                            className="w-full"
                            inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                    </div>
                    <div className="md:col-span-3 flex gap-2">
                        <Button
                            label="Ara"
                            icon="pi pi-search"
                            className="flex-1 !bg-orange-600 border-none hover:!bg-orange-700 rounded-xl font-black text-xs py-3 shadow-md shadow-orange-600/20 text-white"
                            onClick={() => fetchNotes()}
                            loading={loading}
                        />
                        <Button
                            label="Temizle"
                            icon="pi pi-filter-slash"
                            outlined
                            severity="secondary"
                            className="!rounded-xl font-black text-xs py-3"
                            onClick={clearFilters}
                        />
                    </div>
                </div>

                <DataTable value={filteredNotes} loading={loading} paginator rows={10} globalFilter={globalFilter} header={renderHeader()}
                    responsiveLayout="scroll" className="p-datatable-sm mt-2">
                    <Column field="issueDate" header="Tanımlama Tarihi" body={(row) => new Date(row.issueDate).toLocaleDateString('tr-TR')} sortable />
                    <Column field="dueDate" header="Vade" body={(row) => new Date(row.dueDate).toLocaleDateString('tr-TR')} sortable />
                    <Column field="drawer" header="Borçlu" sortable />
                    <Column field="supplierName" header="Verilen Tedarikçi" sortable body={(row) => row.supplierName || <span className="text-slate-400 font-normal">Portföyde</span>} />
                    <Column field="amount" header="Tutar" body={(row) => `₺${row.amount.toLocaleString('tr-TR')}`} sortable />
                    <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                </DataTable>
            </div>

            <Dialog header="Yeni Senet Tanımla" visible={showDialog} style={{ width: '450px' }} modal onHide={() => setShowDialog(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="İptal" icon="pi pi-times" onClick={() => setShowDialog(false)} className="p-button-text" />
                        <Button label="Kaydet" icon="pi pi-check" onClick={saveNote} className="p-button-primary !rounded-xl" />
                    </div>
                }>
                <div className="flex flex-col gap-4 mt-2">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Borçlu</label>
                        <InputText value={newNote.drawer} onChange={(e) => setNewNote({ ...newNote, drawer: e.target.value })} className="!py-2.5 !rounded-xl" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Kefil</label>
                        <InputText value={newNote.guarantor} onChange={(e) => setNewNote({ ...newNote, guarantor: e.target.value })} className="!py-2.5 !rounded-xl" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Vade Tarihi</label>
                        <Calendar value={newNote.dueDate} onChange={(e) => setNewNote({ ...newNote, dueDate: e.value })} dateFormat="dd.mm.yy" showIcon className="!rounded-xl" appendTo={document.body} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Tutar</label>
                        <InputNumber value={newNote.amount} onValueChange={(e) => setNewNote({ ...newNote, amount: e.value })} mode="currency" currency="TRY" locale="tr-TR" className="!rounded-xl" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Açıklama</label>
                        <InputText value={newNote.description} onChange={(e) => setNewNote({ ...newNote, description: e.target.value })} className="!py-2.5 !rounded-xl" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
